// App.js
import React, { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/Home"; // Don't lazy load Home - it's the landing page
import Loader from "./components/Loader";
import "./css/custom.css";
import useCanonical from "./config/useCanonical";
import { Toaster } from "react-hot-toast";

// Lazy load non-critical popups - they load after page render
const CookieNotice = lazy(() => import("./components/CookieNotice"));
const MusicPopup = lazy(() => import("./components/MusicPopup"));

// Code splitting: Lazy load non-critical routes only
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const Projects = lazy(() => import("./pages/Projects"));
const LiveGallery = lazy(() => import("./pages/LiveGallery"));
const Blog = lazy(() => import("./pages/Blog"));
const Contact = lazy(() => import("./pages/Contact"));
const Enquire = lazy(() => import("./pages/Enquire"));
const Terms_And_Conditions = lazy(() => import("./pages/Terms_And_Conditions"));
const Cookie_Policy = lazy(() => import("./pages/Cookie_Policy"));
const Gdpr_Policy = lazy(() => import("./pages/Gdpr_Policy"));

const ProgressLoader = ({ isLoading }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (isLoading) {
      setProgress(0);
      const interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 15;
        });
      }, 100);
      return () => clearInterval(interval);
    } else {
      setProgress(100);
      setTimeout(() => setProgress(0), 500);
    }
  }, [isLoading]);

  if (!isLoading && progress === 0) return null;

  return (
    <div style={{ position: "fixed", top: 0, left: 0, width: "100%", zIndex: 9999 }}>
      <div
        style={{
          height: "3px",
          backgroundColor: "#5a4140",
          width: `${progress}%`,
          transition: "all 0.3s ease-out",
          boxShadow: "0 0 10px rgba(163, 163, 163, 0.5)",
        }}
      />
    </div>
  );
};

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [pathname]);

  return null;
}
const usePageTransition = () => {
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  return isLoading;
};

function PageWrapper() {
    useCanonical();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const isLoading = usePageTransition();

  useEffect(() => {
    if (location.pathname === "/") {
      setLoading(true);
      const handleLoad = () => setLoading(false);
      window.addEventListener("load", handleLoad);
      return () => window.removeEventListener("load", handleLoad);
    } else {
      setLoading(false);
    }
  }, [location.pathname]);

  return (
    <>
      <ProgressLoader isLoading={isLoading} />
      <ScrollToTop />

      <Header />

      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={
          <Suspense fallback={<div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>}>
            <About />
          </Suspense>
        } />
        <Route path="/services" element={
          <Suspense fallback={<div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>}>
            <Services />
          </Suspense>
        } />
        <Route path="/service-detail/:id" element={
          <Suspense fallback={<div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>}>
            <ServiceDetail />
          </Suspense>
        } />
        <Route path="/projects" element={
          <Suspense fallback={<div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>}>
            <Projects />
          </Suspense>
        } />

            <Route path="/livegallery" element={
          <Suspense fallback={<div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>}>
            <LiveGallery />
          </Suspense>
        } />
        <Route path="/contact" element={
          <Suspense fallback={<div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>}>
            <Contact />
          </Suspense>
        } />
        <Route path="/enquire" element={
          <Suspense fallback={<div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>}>
            <Enquire />
          </Suspense>
        } />
        <Route path="/cookie_policy" element={
          <Suspense fallback={<div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>}>
            <Cookie_Policy />
          </Suspense>
        } />
        <Route path="/gdpr_policy" element={
          <Suspense fallback={<div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>}>
            <Gdpr_Policy />
          </Suspense>
        } />
        <Route path="/terms_and_conditions" element={
          <Suspense fallback={<div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>}>
            <Terms_And_Conditions />
          </Suspense>
        } />
        <Route path="/blog" element={
          <Suspense fallback={<div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>}>
            <Blog />
          </Suspense>
        } />
        <Route path="/blog/:slug" element={
          <Suspense fallback={<div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>}>
            <BlogDetail />
          </Suspense>
        } />
      </Routes>
      <Suspense fallback={null}>
        <CookieNotice />
      </Suspense>
      <Footer />

      {loading && location.pathname === "/" && <Loader />}

      <Suspense fallback={null}>
        <MusicPopup />
      </Suspense>
    </>
  );
}

function App() {
  return (
    <Router>
            <Toaster position="top-right" />
      <PageWrapper />
    </Router>
  );
}

export default App;
